/**
 * FollowupReminder · iter303ak — Quote Sent · Task 50 (Follow-up)
 * ─────────────────────────────────────────────────────────────────
 * Sets the follow-up reminder date on the appraisal. Defaults to
 * +N business days from now:
 *   • N = params.urgent_days (default 3) when appraisal_priority ===
 *     'urgent'
 *   • N = params.default_days (default 7) otherwise
 *
 * Operator can pick any date via the datetime-local input or use the
 * three quick-pick pills (+3d urgent / +7d standard / +10d relaxed).
 *
 * Writes to captured_data (via write_to, default
 * 'appraisal.quote_followup_due_at') AND PATCHes /appraisals/{id}
 * with quote_followup_due_at = ISO string.
 *
 * iter303ak-fix22 · Task title renamed from "Follow-up & Handoff" to
 * "Follow-up" — job shell seeding lives in later statuses now, so
 * this task only manages the reminder date.
 */
import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { CalendarClock, CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const pad = n => String(n).padStart(2, '0');
const isoLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Advance by N business days (skips Sat / Sun).
const addBusinessDays = (start, n) => {
  const d = new Date(start);
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added += 1;
  }
  // Reset to 09:00 local so the reminder lands during work hours.
  d.setHours(9, 0, 0, 0);
  return d;
};

export default function FollowupReminder({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const defaultDays = params.default_days || 7;
  const urgentDays = params.urgent_days || 3;
  const relaxedDays = params.relaxed_days || 10;
  const appraisalId = capturedData?.appraisal_id;

  // iter303ak-fix22 · Detect urgent flag. Trevor's schema:
  // `appraisal_priority === 'urgent'` on the appraisal record. Fall
  // back to false so non-urgent flows are unaffected.
  const app = capturedData?.appraisal || capturedData?.appraisal_record || {};
  const isUrgent = (app?.appraisal_priority || '').toLowerCase() === 'urgent';
  const activeDays = isUrgent ? urgentDays : defaultDays;

  const defaultIso = useMemo(() => isoLocal(addBusinessDays(new Date(), activeDays)), [activeDays]);
  const [when, setWhen] = useState(value || app?.quote_followup_due_at || defaultIso);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (onChange) onChange(when); }, [when, onChange]);

  const setBusinessDays = (n) => {
    setWhen(isoLocal(addBusinessDays(new Date(), n)));
  };

  const advance = async () => {
    if (!when) { setErr('Pick a follow-up date + time.'); return; }
    setSaving(true); setErr('');
    try {
      const asDate = new Date(when);
      const iso = asDate.toISOString();
      if (appraisalId) {
        await api.patch(`/appraisals/${appraisalId}`, {
          quote_followup_due_at: iso,
        });
      }
      onNext();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const pillClass = (n) => {
    const currentDaysFromWhen = when ? Math.round((new Date(when) - new Date()) / (1000 * 60 * 60 * 24)) : null;
    // Rough active-state highlight — matches when the picker is within
    // ±1 calendar day of the pill's business-day target.
    const active = currentDaysFromWhen != null && Math.abs(currentDaysFromWhen - n) <= 1;
    return `px-3 py-1.5 rounded-full text-xs font-medium border transition ${
      active
        ? 'bg-sky-600 border-sky-600 text-white'
        : 'bg-white border-slate-300 text-slate-700 hover:border-sky-400 hover:bg-sky-50'
    }`;
  };

  return (
    <div className="space-y-4" data-testid="wf-followup-reminder">
      <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
        <div className="flex items-start gap-3">
          <CalendarClock className="w-5 h-5 text-sky-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-sky-900 flex items-center gap-2">
              Follow-up Reminder
              {isUrgent && (
                <span
                  data-testid="wf-followup-urgent-badge"
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-orange-800 bg-orange-100 border border-orange-300 rounded px-1.5 py-0.5"
                  title="Appraisal is flagged urgent — default reduced to +3 business days"
                >
                  <AlertTriangle className="w-3 h-3" /> URGENT · {urgentDays}-day follow-up
                </span>
              )}
            </div>
            <div className="text-sm text-sky-800 mt-1">{params.prompt || `Set the follow-up date. Defaults to +${activeDays} business days from now.`}</div>
          </div>
        </div>
      </div>

      <div className="p-4 border border-slate-200 rounded-lg bg-white space-y-3">
        {/* Quick-pick pills */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Quick pick</div>
          <div className="flex flex-wrap gap-2" data-testid="wf-followup-quick-picks">
            <button
              onClick={() => setBusinessDays(urgentDays)}
              className={pillClass(urgentDays)}
              data-testid="wf-followup-pill-urgent"
              title="For urgent clients — 3 business days"
            >
              +{urgentDays}d urgent
            </button>
            <button
              onClick={() => setBusinessDays(defaultDays)}
              className={pillClass(defaultDays)}
              data-testid="wf-followup-pill-standard"
              title="Standard follow-up — 7 business days"
            >
              +{defaultDays}d standard
            </button>
            <button
              onClick={() => setBusinessDays(relaxedDays)}
              className={pillClass(relaxedDays)}
              data-testid="wf-followup-pill-relaxed"
              title="Longer window — 10 business days"
            >
              +{relaxedDays}d relaxed
            </button>
          </div>
        </div>

        {/* Datetime input */}
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-2">Or pick a specific date + time…</label>
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="w-full max-w-xs px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            data-testid="wf-followup-datetime"
          />
          <div className="text-xs text-slate-500 mt-2">
            When the follow-up comes due, we&rsquo;ll surface a queued reminder card in Operator Queue with 3 actions: send reminder (email/SMS/call log), mark declined, or offer a revised price.
          </div>
        </div>
      </div>

      {err ? (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-followup-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel ? (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-followup-cancel">Cancel</button>
          ) : null}
          <button
            onClick={advance}
            disabled={saving || !when}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-followup-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Set Reminder & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
